import Axios from 'axios'
import { useSelector } from 'react-redux'
import { Suspense, useEffect, useRef } from 'react'
import { useSafeState, useMount, useLockFn } from 'ahooks'
import { BrowserRouter as Router } from 'react-router-dom'
import { App as AntDesignApp, ConfigProvider, Flex } from 'antd'

import '@/utils/checkoutLeave'
import keys from '@/config/keys'
import store from '@/redux/store'
import isEmpty from '@/utils/isEmpty'
import appFetch from '@/utils/appFetch'
import loadable from '@/utils/loadable'
import endpoints from '@/config/endpoints'
import { ELO } from '@/utils/loadableHelpers'
import sanitizeLink from '@/utils/sanitizeLink'
import { showNotification } from '@/utils/notify'
import { getErrorAlert } from '@/utils/getErrorAlert'
import { getAppSettings } from '@/utils/getAppSettings'
// import initCrossTabAuthSync from '@/utils/crossTabAuth'
import getTerminologyLabel from '@/utils/getTerminology'
import { setCurrentUser } from '@/redux/slices/authSlice'
import getAppRouteBasename from '@/utils/getAppRouteBasename'
import { getAntDThemeToken } from '@/utils/getAntDThemeToken'
import LoadingCenter from '@/components/Loading/LoadingCenter'
import setAxiosDefaultHeader from '@/utils/setAxiosDefaultHeader'
import { setParallaxProps } from '@/redux/slices/navigationSlice'
import { setIsAccountTabMenu } from '@/redux/slices/accountSlice'
import AppSkeletonBase from '@/components/Loading/AppSkeletonBase'
import { addUserPusherChannels } from '@/redux/slices/pusherSlice'
import { captureAndSaveTrackingParams } from '@/utils/utmTracking'
import WafCaptchaModal from '@/components/security/WafCaptchaModal'
import { installWidgetAxiosInterceptor } from '@/utils/widgetSession'
import { AntdAppProvider, useAntdApp } from '@/context/antdAppContext'
import { resetThemeData, setThemeData } from '@/redux/slices/themeSlice'
import useFormAccessibilityStyles from '@/hooks/useFormAccessibilityStyles'
import isEnhanceFormFieldAccessibility from '@/utils/formFieldAccessibility'
import { getAllQueryVariables, getQueryVariable } from '@/utils/getQueryVariable'
import { createWidgetResizeMessenger, useWidgetScrollListener } from './utils/eldWidget'
import { consumePendingLoginWelcome, markLoginWelcomePending } from '@/utils/loginWelcome'

const COMMON_ERR_MSG = `Something went wrong. Please refresh & try again!`
const SETTINGS_NOT_FOUND_FALLBACK = {
	heading: `Whoops, this site doesn't exist`,
	sentence: `Please double check the URL before trying again.
Alternatively check with your account or event manager for more information.`,
}

const BaseView = loadable(() => import('@/BaseView'))

const AppBanner = loadable(() => import('@/components/micro/AppBanner'), ELO)
const PageElement = loadable(() => import('@/components/micro/PageElement'), ELO)
const CookieBanner = loadable(() => import('@/components/micro/CookieBanner'), ELO)
const HelmetFavicon = loadable(() => import('@/components/micro/HelmetFavicon'), ELO)

const Body = () => {
	const [error, setError] = useSafeState('')
	const [loading, setLoading] = useSafeState(true)
	const [checking, setChecking] = useSafeState(true)
	const settings = useSelector((state) => state.settings)
	const isAccessibilityEnhanced = isEnhanceFormFieldAccessibility(settings)

	const qsVars = getAllQueryVariables() || {}

	return (
		<>
			<HelmetFavicon />
			<ConfigProvider
				theme={{
					cssVar: true,
					hashed: false,
					token: getAntDThemeToken(isAccessibilityEnhanced),
				}}
			>
				<AntDesignApp className='w-full' id={keys.APP_BODY_ID}>
					<AntdAppProvider>
						<WafCaptchaModal />
						<BodyInner
							qsVars={qsVars}
							error={error}
							loading={loading}
							checking={checking}
							setError={setError}
							setLoading={setLoading}
							setChecking={setChecking}
						/>
					</AntdAppProvider>
				</AntDesignApp>
			</ConfigProvider>
		</>
	)
}

export default Body

const BodyInner = ({ loading, error, checking, setLoading, setError, setChecking, qsVars }) => {
	const [settings, setSettings] = useSafeState(undefined)
	const [terminologyLoading, setTerminologyLoading] = useSafeState(true)
	const [settingsNotFound, setSettingsNotFound] = useSafeState(false)
	const { notification } = useAntdApp()
	const redirectingRef = useRef(false)

	const [embedTokenLoading, setEmbedTokenLoading] = useSafeState(false)
	const [embedTokenError, setEmbedTokenError] = useSafeState(false)
	const embedView = useSelector((state) => state.theme.embedView)
	const loggedInUser = useSelector((state) => state.auth.user)
	const isWidgetView = embedView === keys.EMBED_VIEW.widget
	useWidgetScrollListener(isWidgetView)
	useFormAccessibilityStyles()

	const getSettings = useLockFn(async () => {
		try {
			setLoading(true)
			const settingsData = await getAppSettings()

			if (!settingsData) return setError(COMMON_ERR_MSG)

			if (settingsData.password_protectedYN) {
				try {
					const ep = endpoints.withSuffix(`/site-password`)
					const { data: res } = await Axios.get(ep)
					console.log('Site password res: ', res)
				} catch (error) {
					console.log('Site password error:', error)
					const statusCode = error?.response?.status
					if (statusCode === 401) store.dispatch(setThemeData({ appLocked: true }))
				}
			}

			setSettings(settingsData)

			const lastSegment = location.pathname.split('/').filter(Boolean).pop()
			const info = sanitizeLink(`/${lastSegment}`, { needInfo: true })
			if (info?.isModified) {
				const { url_root_path } = settingsData
				const src = `${window.location.origin}${url_root_path === '/' || !url_root_path ? '' : `/${url_root_path}`}${info?.link}`
				window.location.href = src
			}

			setLoading(false)
			return settingsData
		} catch (error) {
			const statusCode = error?.response?.status
			if (statusCode === 404) {
				setSettingsNotFound(true)
				setLoading(false)
				return
			}

			setError(COMMON_ERR_MSG)
		}
	})

	const checkUserSession = async () => {
		try {
			setChecking(true)

			let ep = endpoints.user_profile
			const userToken = qsVars[keys.QUERY_PARAMS.USER_TOKEN]
			if (userToken) ep += `?${keys.QUERY_PARAMS.USER_TOKEN}=${userToken}`
			const { data: res } = await Axios.get(ep)

			console.log('App User Profile: ', res)
			store.dispatch(setCurrentUser(res?.user))
			store.dispatch(addUserPusherChannels(res?.pusher_channels))
			if (userToken && res?.user) markLoginWelcomePending()

			const { redirect_route, base_url } = res || {}

			if (redirect_route) {
				const current_path = window.location.pathname

				const url = new URL(base_url)
				const root_path = url.pathname
				let src = `${window.location.origin}${root_path === '/' ? '' : root_path}${redirect_route}`
				const prefix = src.includes('?') ? '&' : '?'
				const qsArr = []

				// Attach '_redirect' query param that will contain the path that user is currently trying to go along with existing query params except user token
				if (
					!src.includes(`${keys.QUERY_PARAMS.REDIRECT}=`) &&
					!(keys.QUERY_PARAMS.REDIRECT in qsVars) &&
					current_path !== redirect_route
				) {
					const arr = []
					Object.keys(qsVars).forEach((key) => {
						if (key !== keys.QUERY_PARAMS.USER_TOKEN) arr.push(`${key}=${qsVars[key]}`)
					})
					const pure_current_path = current_path?.replace?.(root_path, '').trim?.()
					if (pure_current_path) {
						const val = pure_current_path + (!isEmpty(arr) ? `?${arr.join('&')}` : '')
						qsArr.push(`${keys.QUERY_PARAMS.REDIRECT}=${encodeURIComponent(val)}`)
					}
				}

				// Attach existing query params except user token
				Object.keys(qsVars).forEach((key) => {
					if (key !== keys.QUERY_PARAMS.USER_TOKEN) qsArr.push(`${key}=${qsVars[key]}`)
				})
				if (!isEmpty(qsArr)) src += `${prefix}${qsArr.join('&')}`

				/* Can't use react-router-dom as it's not yet instantiated */
				console.log('Redirecting to: ', src)
				redirectingRef.current = true
				window.location.href = src
			}
		} catch (error) {
			console.log('User needs to login.', error)
		} finally {
			setChecking(false)
		}
	}

	const getTerminology = async () => {
		try {
			const { data } = await Axios.get(endpoints.terminology, { withCredentials: false })
			console.log('App Terminology: ', data)
			window[keys._TERMINOLOGY_] = data
		} catch (error) {
			console.log('Terminology load error', error)
		} finally {
			setTerminologyLoading(false)
		}
	}

	const checkEmbedToken = async () => {
		const params = new URLSearchParams(window.location.search)

		try {
			const isWidgetParam = params.get(keys.QUERY_PARAMS.WIDGET) === '1'
			const isWidgetDomain = window.location.hostname.endsWith('.widget.elnk.co')
			const tokenParam = params.get(keys.QUERY_PARAMS.WIDGET_TOKEN)
			if (isWidgetParam || isWidgetDomain) {
				if (!tokenParam) throw new Error('Widget token is missing.')

				setEmbedTokenLoading(true)

				const res = await appFetch(endpoints.validate_embed_token, {
					method: 'POST',
					headers: { 'Content-Type': 'text/plain' },
					body: JSON.stringify({ token: tokenParam }),
				})

				const data = await res.json().catch(() => ({}))

				if (!res.ok) {
					throw new Error(data?.error || `Widget token validation failed (${res.status}).`)
				}

				store.dispatch(setThemeData({ embedMetaData: data }))
			}
		} catch (error) {
			console.log('Embed token error', error)
			setEmbedTokenError(true)
		} finally {
			setEmbedTokenLoading(false)
		}
	}

	const bootstrapApp = async () => {
		try {
			// In widget mode skip the session/profile call — no auth cookie is sent.
			// setChecking(false) must be called explicitly so the app doesn't stay
			// stuck on the skeleton (checking starts as true and only checkUserSession
			// ever resets it via its finally block).
			const promises = [getSettings(), getTerminology(), checkEmbedToken()]
			if (!isWidgetView) {
				promises.push(checkUserSession())
			} else {
				setChecking(false)
			}

			await Promise.allSettled(promises)

			// getAppSettings() enables withCredentials globally; override for widget mode
			if (isWidgetView) setAxiosDefaultHeader(false)

			// Keep tabs in sync: once the session check has settled, a login/logout
			// (i.e. an auth state change) in any tab reloads the others. Skipped in
			// widget mode, which has no cross-tab cookie session.
			// if (!isWidgetView) initCrossTabAuthSync()
		} catch (err) {
			console.log(err)
		}
	}

	useMount(() => {
		captureAndSaveTrackingParams()

		const browserUIHeight = window.outerHeight - window.innerHeight
		const actualViewportHeight =
			window.visualViewport?.height || window.innerHeight - browserUIHeight
		document.documentElement.style.setProperty(
			'--actual-viewport-height',
			`${actualViewportHeight}px`,
		)

		const VITE_NODE_ENV = import.meta.env.VITE_NODE_ENV
		console.log('Environment:', VITE_NODE_ENV)
		/* Disable console.log in prod */
		if (VITE_NODE_ENV === 'production') {
			const isDebugChecked = getQueryVariable('debug') || ''
			if (!isDebugChecked) console.log = () => {}
		}

		// Reset Redux Persisted State
		store.dispatch(resetThemeData({}))
		store.dispatch(setParallaxProps({}))
		store.dispatch(setIsAccountTabMenu(true))

		// Must run before bootstrapApp so every Axios request (settings, terminology,
		// checkout, payment …) has withCredentials forced off in widget mode.
		// An interceptor — not a one-time default override — is required because
		// getAppSettings() calls setAxiosDefaultHeader(true) and can be re-invoked
		// on subsequent route changes (e.g. withPageSchema HOC).
		if (isWidgetView) installWidgetAxiosInterceptor()

		bootstrapApp()

		AppBanner.preload()
		PageElement.preload()
		CookieBanner.preload()
		HelmetFavicon.preload()
	})

	useEffect(() => {
		const widgetResize = createWidgetResizeMessenger()
		const session = widgetResize.start()
		window.eldWidgetResize = widgetResize
		return function () {
			delete window.eldWidgetResize
			session.stop()
		}
	}, [])

	useEffect(() => {
		if (
			loading ||
			checking ||
			terminologyLoading ||
			embedTokenLoading ||
			redirectingRef.current ||
			!loggedInUser ||
			!consumePendingLoginWelcome()
		) {
			return
		}

		const welcomeBack = getTerminologyLabel('welcome_back', null, false)
		const firstName = String(loggedInUser.first_name || '').trim()
		const message = `${welcomeBack}${firstName ? ` ${firstName}` : ''}`

		showNotification({
			notification,
			message: (
				<Flex align='center' justify='center' className='ml-6'>
					<span>{message}</span>
				</Flex>
			),
		})
	}, [loading, checking, terminologyLoading, embedTokenLoading, loggedInUser, notification])

	if (loading && error) return <LoadingCenter msg={error} msgColor='tomato' simple={true} />

	if (settingsNotFound) {
		if (terminologyLoading) {
			return <LoadingCenter spin={true} style={{ backgroundColor: 'var(--body-bg-color)' }} />
		}

		return (
			<Router>
				<SettingsNotFound />
			</Router>
		)
	}

	if (embedTokenError) {
		return getErrorAlert({ msg: 'Unable to load widget.', className: 'm-5' })
	}

	const DEFAULT_LOADING_UI = <AppSkeletonBase showHeader={!isWidgetView} />
	if (loading || checking || terminologyLoading || embedTokenLoading) return DEFAULT_LOADING_UI

	const router_basepath = getAppRouteBasename(settings)

	return (
		<Router basename={router_basepath}>
			<main className='w-full'>
				{!isWidgetView && <AppBanner />}
				<Suspense fallback={DEFAULT_LOADING_UI}>
					<BaseView />
				</Suspense>
				{!isWidgetView && <CookieBanner />}
			</main>
		</Router>
	)
}

const SettingsNotFound = () => {
	const heading =
		getTerminologyLabel('notfound.heading', null, false, true) ||
		SETTINGS_NOT_FOUND_FALLBACK.heading
	const sentence =
		getTerminologyLabel('notfound.sentence', null, false, true) ||
		SETTINGS_NOT_FOUND_FALLBACK.sentence

	const pageElements = [
		{
			_cols: 24,
			_withCard: true,
			_title: heading,
			_info: String(sentence).replace(/\n/g, '<br />'),
			heading_size: 'h2',
			heading_alignment: 'Center',
		},
	]

	return (
		<main className='flex min-h-screen w-full justify-center bg-[--body-bg-color] px-4 py-24 sm:py-32'>
			<div className='w-full max-w-2xl'>
				<PageElement _page_elements={pageElements} />
			</div>
		</main>
	)
}
